import { atom, atomFamily, selector, selectorFamily } from "recoil";
import { selectedWeatherList, WX_SETTINGS } from "./WeatherContentsState";

/**
 * 高度スライダーの選択範囲
 * @type {RecoilState<number[]>}
 */
export const altitudeState = atom({
  key: 'altitudeState',
  default: [0, 530]
});

/**
 * 高度スライダーのラベル表示フォーマット
 */
export const rangeAltitudeLabel = selector({
  key: 'rangeAltitudeLabel',
  get: ({get}) => {
    const alt = get(altitudeState);
    const low = alt[0];
    const high = alt[1];
    let fl;
    if(low === 0) {
      fl = 'SFC';
    } else {
      fl = `FL${('000' + low).slice(-3)}`;
    }
    if(low !== high) {
      fl += ` - FL${('000' + high).slice(-3)}`;
    }
    return fl;
  }
});

/**
 * 高度スライダーの表示可否(true:表示,false非表示)
 * @type {RecoilValueReadOnly<boolean>}
 */
export const altitudeVisibleState = selector({
  key: 'altitudeVisibleState',
  get: ({get}) => {
    const wList = get(selectedWeatherList);
    const idx =  wList.findIndex(e => {
      return WX_SETTINGS[e].altitude !== false
    });
    return idx !== -1;
  }
});

/**
 * 高度スライダーの操作可否
 * @type {RecoilValueReadOnly<boolean>}
 */
export const rangeAltitudeEnableState = selector({
  key: 'rangeAltitudeEnableState',
  get: ({get}) => {
    const wList = get(selectedWeatherList);
    const idx =  wList.findIndex(e => {
      return WX_SETTINGS[e].altitude === 'range'
    });
    return idx !== -1;
  }
});

/**
 * 個別高度(UKMET等)のatomFamily
 */
const individualAltitudeFamily = atomFamily({
  key: 'individualAltitudeFamily',
  default: 0
});

/**
 * 個別高度(UKMET等)のselectorFamily
 */
export const individualAltitudeWithID = selectorFamily({
  key: 'individualAltitudeWithID',
  get: (id) => ({get}) => {
    return get(individualAltitudeFamily(id));
  },
  set: (id) => ({set, get}, altitude) => {
    set(individualAltitudeFamily(id), altitude);
    const oldList = get(altitudeContentsListState);
    const idx = oldList.findIndex(c => {
      return c.id === id;
    });
    if (idx === -1) return;

    const tmp = {...oldList[idx]};
    tmp.altitude = altitude;
    const newList = [...oldList];
    newList[idx] = tmp;
    set(altitudeContentsListState, newList);
  }
});

/**
 * 個別高度(UKMET等)の選択中リスト
 * @type {RecoilState<*[]>}
 */
export const altitudeContentsListState = atom({
  key: 'altitudeContentsListState',
  default: []
});

/**
 * 高度情報を個別高度の選択中リストに追加する
 */
export const addIndividualAltitudeSelector = selector({
  key: 'addIndividualAltitudeSelector',
  set: ({get, set}, altitude) => {
    const oldList = get(altitudeContentsListState);
    const idx = oldList.findIndex(target => {
      return target.id === altitude.id;
    });
    if (idx !== -1) return;
    const newList = [...oldList, altitude];
    set(altitudeContentsListState, newList);
  },
  get: () => {},
  dangerouslyAllowMutability: true
});

/**
 * 高度情報を個別高度の選択中リストから削除する
 */
export const removeIndividualAltitudeSelector = selector({
  key: 'removeIndividualAltitudeSelector',
  set: ({get, set}, altitude) => {
    const oldList = get(altitudeContentsListState);
    const idx = oldList.findIndex(target => {
      return target.id === altitude.id;
    });
    if (idx === -1) return;

    const newList = [...oldList.slice(0, idx), ...oldList.slice(idx + 1)];
    set(altitudeContentsListState, newList);
  },
  get: () => {},
  dangerouslyAllowMutability: true
});

/**
 * 引数に指定したコンテンツを最前面に個別高度の選択中リストを並び替える
 * @type {RecoilState<unknown>}
 */
export const setForegroundAltitudeSelector = selector({
  key: 'setForegroundAltitudeSelector',
  set: ({get, set}, id) => {
    const contentsList = [...get(altitudeContentsListState)];
    const idx = contentsList.findIndex(content => {
      return content.id === id;
    });

    if (idx === -1) return;

    if (idx !== contentsList.length - 1) {
      //前面
      const p = contentsList.splice(idx, 1)[0];
      contentsList.push(p);
    } else {
      //背面
      const p = contentsList.splice(idx, 1)[0];
      contentsList.unshift(p);
    }
    set(altitudeContentsListState, contentsList);
  },
  get: () => {}
});

/**
 * タイムスライダーによって更新される飛行中の高度
 * @type {RecoilState<number>}
 */
export const individualAltByTimeControllerState = atom({
  key: 'individualAltByTimeControllerState',
  default: 0
});

/**
 * 個別高度ラベルのatomFamily
 */
export const individualAltitudeLabelWithID = atomFamily({
  key: 'individualAltitudeLabelFamily',
  default: ''
});
